import { useState, useEffect } from 'react';
import { startOf } from 'next/lib/date';

import {
  ABSOLUTE_TIME_OPERATORS,
  DEFAULT_OPERATORS,
  MULTI_VALUE_OPERATORS,
  NUMERIC_OPERATORS,
  RELATIVE_TIME_OPERATORS,
  EXISTENTIAL_OPERATORS,
  MILLISECONDS_PER_DAY,
} from './constants';

/**
 * Given a condition and onChange function, returns all of the required
 * booleans & event handler callbacks to control a property-type clause
 *
 * @param innerConditions
 * @param onChange
 */
export default (innerConditions, onChange) => {
  const { operator, value } = innerConditions;

  const hasOptions = MULTI_VALUE_OPERATORS.has(operator);
  const isRelativeTimeOperator = RELATIVE_TIME_OPERATORS.has(operator);
  const isAbsoluteTimeOperator = ABSOLUTE_TIME_OPERATORS.has(operator);
  const isNumericOperator = NUMERIC_OPERATORS.has(operator);
  const isDefaultOperator = DEFAULT_OPERATORS.has(operator);
  const isMultiValueOperator = MULTI_VALUE_OPERATORS.has(operator);
  const isExistentialOperator = EXISTENTIAL_OPERATORS.has(operator);

  /**
   * We're storing the current "value" in component state because this
   * is a controlled component, but value must only bubble onBlur.
   */

  const [draftValue, setDraftValue] = useState(value);

  const handleCurrentValueChange = ({ target: { value: nextValue } }) => {
    if (isMultiValueOperator) {
      setDraftValue(nextValue.split(',').join('\n'));
    } else {
      setDraftValue(nextValue);
    }
  };

  /**
   * Because we are shadowing the "value" in state, we must listen for
   * external changes and apply them to our copy.  This can happen
   * if an operator change results in a value change.
   */

  useEffect(() => {
    if (isRelativeTimeOperator) {
      setDraftValue(value / MILLISECONDS_PER_DAY);
    } else {
      setDraftValue(value);
    }
  }, [isRelativeTimeOperator, value]);

  const handlePropertyChange = ({ value: newProperty }) =>
    void onChange({ ...innerConditions, property: newProperty });

  const handleOperatorChange = ({ value: newOperator }) => {
    let nextValue = value;

    if (MULTI_VALUE_OPERATORS.has(newOperator) && !isMultiValueOperator) {
      nextValue = '';
    }

    if (NUMERIC_OPERATORS.has(newOperator) && !isNumericOperator) {
      nextValue = 1;
    }

    if (RELATIVE_TIME_OPERATORS.has(newOperator) && !isRelativeTimeOperator) {
      nextValue = 24 * 60 * 60 * 1000;
    }

    if (ABSOLUTE_TIME_OPERATORS.has(newOperator) && !isAbsoluteTimeOperator) {
      nextValue = startOf(new Date(), 'day').valueOf();
    }

    if (EXISTENTIAL_OPERATORS.has(newOperator)) {
      nextValue = null;
    }

    if (DEFAULT_OPERATORS.has(newOperator) && !isDefaultOperator) {
      nextValue = '';
    }

    onChange({
      ...innerConditions,
      operator: newOperator,
      value: nextValue,
    });
  };

  const handleValueAsDateChange = newValue =>
    void onChange({
      ...innerConditions,
      value: startOf(newValue, 'day').valueOf(),
    });

  const handleValueBlur = ({ target: { value: newValue, valueAsNumber } }) => {
    let nextValue = newValue;

    if (isRelativeTimeOperator) {
      const numberOfDays = valueAsNumber || 1;
      nextValue = numberOfDays * MILLISECONDS_PER_DAY;
    }

    if (isNumericOperator) {
      nextValue = valueAsNumber || 1;
    }

    onChange({ ...innerConditions, value: nextValue });
  };

  const handleValueSelected = ({ selectedItem }) =>
    void onChange({ ...innerConditions, value: selectedItem });

  return {
    draftValue,
    hasOptions,
    isRelativeTimeOperator,
    isAbsoluteTimeOperator,
    isNumericOperator,
    isDefaultOperator,
    isMultiValueOperator,
    isExistentialOperator,
    onPropertyChange: handlePropertyChange,
    onOperatorChange: handleOperatorChange,
    onValueChange: handleCurrentValueChange,
    onDateChange: handleValueAsDateChange,
    onValueBlur: handleValueBlur,
    onValueSelected: handleValueSelected,
  };
};
